import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import { AlertCircle, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useKidStore, type KidWithDetails } from '../../stores/kidStore';
import { completudeEnfant, completudeProfil } from '../../lib/completude';
import { ModalePaiement } from '../../components/checkout/ModalePaiement';
import { useDevis } from '../../lib/orders/useDevis';
import { commander, ErreurDeCommande, type LigneCommande } from '../../lib/orders/gateway.supabase';
import { adresseDeConfirmation, ecritureDuChoix, sortieDeLaFenetre } from '../../lib/orders/tunnel';
import { SEUIL_REVENUS_CENTIMES, euros, texteDeclaration, type Commande } from '../../lib/orders/devis';
import { offreDePaiement, type OffreDePaiement } from '../../lib/paiement/stripe';
import { payerParStripe, ErreurDePaiement } from '../../lib/paiement/gateway.supabase';
import {
  REGLE_ANNULATION_JPA,
  avertissementsDeLEcole,
  dateCourte,
  dateLongue,
  ecoleProposee,
  ecolesDesJournees,
  enfantAuChoix,
  etatDeLaJournee,
  etatFaceALEcole,
  heure,
  instantLisible,
  jourABruxelles,
  prenomsEnToutesLettres,
  prixDuMoment,
  type ClasseDuCentre,
  type EnfantAuChoix,
  type JourneeJpa,
} from '../../lib/jpa/regles';
import {
  inscriptionsDeLaFamille,
  lienDesConditions,
  lireClasses,
  lireClassesDesEnfants,
  lireJournees,
} from '../../lib/jpa/gateway.supabase';
import EtapesDuParcours from './parcours/EtapesDuParcours';
import { ETAPES_DU_PARCOURS, type EtapeDuParcours } from './parcours/etapes';
import ChoixDeLEcole from './parcours/ChoixDeLEcole';
import EnfantACocher from './parcours/EnfantACocher';
import AvertissementsDeLEcole from './parcours/AvertissementsDeLEcole';
import JourneesDeLEnfant from './parcours/JourneesDeLEnfant';

/*
  La page des journées pédagogiques, côté parent : quatre étapes, une question
  à la fois (VALIDÉ ÉCLOSION, 24/10/2026).

  1. L'école : celle des enfants, proposée d'après leur fiche.
  2. « Quels enfants souhaitez-vous inscrire ? » (26/10/2026) : le parent
     coche ; vert, l'enfant peut participer ; orange, sa classe dans cette
     école ou sa fiche manque, et on la lui demande une fois coché. Rien
     n'est coché d'office, sauf l'enfant unique d'un compte s'il est vert.
  3. Les journées des enfants cochés, le total en bas d'écran.
  4. Le récapitulatif : tarif réduit, conditions générales, puis le paiement.

  Avant, toutes les écoles s'affichaient ensemble, avec sous chacune tous les
  enfants de la famille et la même question pour chacun ; le tarif réduit et
  les conditions étaient là dès l'arrivée.

  Une JPA réutilise le tunnel des stages — devis, commande au choix du
  paiement, Bancontact ou virement, page de confirmation — sans passer par le
  panier : une journée se commande à part (la base refuse un panier qui mêle
  JPA et stages). La page confirme donc avec `panier=garder`, pour que la
  confirmation ne vide pas un panier de stages en cours.

  L'éligibilité — élève de l'école, classe admise, au plus tard le jour même —
  est tranchée par la base au moment de la commande. Cette page la dit avant :
  un enfant qui ne peut pas être inscrit montre ce qui manque, et le geste
  pour le régler, au lieu d'une case qu'un refus contredirait au clic.
*/

const cle = (sessionId: string, kidId: string) => `${sessionId}|${kidId}`;

/** L'adresse de confirmation d'une commande JPA : elle ne vide pas le panier des stages. */
const confirmationJpa = (c: Commande) => `${adresseDeConfirmation(c)}&panier=garder`;

/** Les horaires d'une journée, comme on les dit. */
const horaires = (j: JourneeJpa) =>
  `accueil ${heure(j.accueilDebut)} – ${heure(j.accueilFin)}, activités ${heure(j.activitesDebut)} – ${heure(j.activitesFin)}`;

export default function JourneesPedagogiquesPage() {
  const navigate = useNavigate();
  const [parametres, setParametres] = useSearchParams();
  const { user, profile, fetchProfile } = useAuthStore();
  const { kids, fetchKids } = useKidStore();

  const [journees, setJourneees] = useState<JourneeJpa[]>([]);
  const [classes, setClasses] = useState<ClasseDuCentre[]>([]);
  /** année scolaire → enfant → classe */
  const [classesDesEnfants, setClassesDesEnfants] = useState<Map<number, Map<string, ClasseDuCentre>>>(new Map());
  const [inscrits, setInscrits] = useState<Map<string, Set<string>>>(new Map());
  const [lienCG, setLienCG] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreurDeChargement, setErreurDeChargement] = useState<string | null>(null);

  const [etape, setEtape] = useState<EtapeDuParcours>('ecole');
  /** Le centre de l'école choisie par le parent ; `null` tant qu'il suit la proposition. */
  const [centreChoisi, setCentreChoisi] = useState<string | null>(null);
  /** Les enfants cochés à l'étape 2 ; `null` tant que le parent n'y a pas touché. */
  const [coches, setCoches] = useState<Set<string> | null>(null);

  const [choix, setChoix] = useState<Set<string>>(new Set());
  const [tarifReduit, setTarifReduit] = useState(false);
  const [modaleDeclaration, setModaleDeclaration] = useState(false);
  const [declarationCochee, setDeclarationCochee] = useState(false);
  const [cgAcceptees, setCgAcceptees] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const [fenetreOuverte, setFenetreOuverte] = useState(false);
  const [offre, setOffre] = useState<OffreDePaiement>({ forme: 'rien_a_payer' });
  const [commande, setCommande] = useState<Commande | null>(null);
  const [enCours, setEnCours] = useState<'stripe' | 'virement' | null>(null);
  const [erreurPaiement, setErreurPaiement] = useState<string | null>(null);
  const commandeEnVol = useRef<Promise<Commande | null> | null>(null);

  /* Relu à chaque rendu : la date limite peut passer pendant que la page est ouverte. */
  const maintenant = new Date();

  const charger = useCallback(async () => {
    if (!user) {
      setChargement(false);
      return;
    }
    setChargement(true);
    setErreurDeChargement(null);
    try {
      const lues = (await lireJournees(jourABruxelles(new Date())))
        .filter((j) => etatDeLaJournee(j, new Date()) !== 'passee');
      const [classesLues, inscriptions, lien] = await Promise.all([
        lireClasses([...new Set(lues.map((j) => j.centreId))]),
        inscriptionsDeLaFamille(user.id, lues.map((j) => j.sessionId)),
        lienDesConditions(),
      ]);
      setJourneees(lues);
      setClasses(classesLues);
      setInscrits(inscriptions);
      setLienCG(lien);
    } catch (e) {
      setErreurDeChargement(e instanceof Error ? e.message : String(e));
    } finally {
      setChargement(false);
    }
  }, [user]);

  useEffect(() => { void charger(); }, [charger]);
  useEffect(() => {
    if (user) {
      void fetchKids();
      if (!profile) void fetchProfile();
    }
  }, [user, profile, fetchKids, fetchProfile]);

  /* Les classes des enfants, pour chaque année scolaire des journées affichées. */
  const relireClassesDesEnfants = useCallback(async () => {
    const annees = [...new Set(journees.map((j) => j.anneeScolaire))];
    const ids = kids.map((k) => k.id);
    const paires = await Promise.all(annees.map(async (a) => [a, await lireClassesDesEnfants(ids, a)] as const));
    setClassesDesEnfants(new Map(paires));
  }, [journees, kids]);

  useEffect(() => {
    void relireClassesDesEnfants().catch((e) => setErreurDeChargement(e instanceof Error ? e.message : String(e)));
  }, [relireClassesDesEnfants]);

  const ficheAFaire = useCallback((k: KidWithDetails) => completudeEnfant({
    kids: k,
    health: k.health,
    allergies: k.allergies,
    activities: k.activities,
    departure: k.departure,
    inclusion: k.inclusion,
    image_rights: k.image_rights,
  }).aFaire, []);

  const etatProfil = completudeProfil(profile);

  /* --- L'école ----------------------------------------------------------- */

  const ecoles = useMemo(() => ecolesDesJournees(journees), [journees]);
  const proposee = useMemo(() => ecoleProposee(ecoles, kids.map((k) => k.ecole ?? null)), [ecoles, kids]);
  const centre = centreChoisi ?? proposee;
  const ecole = ecoles.find((e) => e.centreId === centre) ?? null;
  const annee = ecole?.journees[0]?.anneeScolaire ?? null;
  const classesDuCentre = useMemo(() => classes.filter((c) => c.centreId === ecole?.centreId), [classes, ecole]);

  /** Les prénoms des enfants, par école indiquée dans leur fiche. */
  const enfantsParEcole = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const k of kids) {
      const e = (k.ecole ?? '').trim();
      if (e) m.set(e, [...(m.get(e) ?? []), k.prenom]);
    }
    return m;
  }, [kids]);

  /* --- Les enfants face à l'école choisie --------------------------------- */

  const classeDeLAnnee = useCallback(
    (kidId: string) => (annee === null ? null : classesDesEnfants.get(annee)?.get(kidId) ?? null),
    [annee, classesDesEnfants],
  );

  /* Vert, orange, gris ou déjà inscrit : chaque enfant face à l'école choisie. */
  const auChoix = useMemo(() => {
    const m = new Map<string, EnfantAuChoix>();
    if (!ecole) return m;
    for (const k of kids) {
      const etat = etatFaceALEcole(ecole, { ecole: k.ecole ?? null, ficheAFaire: ficheAFaire(k) }, classeDeLAnnee(k.id));
      m.set(k.id, enfantAuChoix(etat, ecole.journees, (sessionId) => inscrits.get(sessionId)?.has(k.id) ?? false));
    }
    return m;
  }, [ecole, kids, ficheAFaire, classeDeLAnnee, inscrits]);

  /*
    Rien n'est coché d'office (retour d'Éclosion, 26/10/2026), sauf l'enfant
    unique d'un compte, s'il peut participer — comme sur la page des stages.
  */
  const cochesEffectifs = useMemo(() => {
    if (coches) return coches;
    const unique = kids.length === 1 ? kids[0] : null;
    return new Set(unique && auChoix.get(unique.id)?.cle === 'peut' ? [unique.id] : []);
  }, [coches, kids, auChoix]);

  const basculerEnfant = (kidId: string) => {
    setErreur(null);
    const apres = new Set(cochesEffectifs);
    if (apres.has(kidId)) apres.delete(kidId);
    else apres.add(kidId);
    setCoches(apres);
  };

  /* Ceux qui passent aux journées : cochés, et verts. */
  const participants = useMemo(
    () => kids.flatMap((k) => {
      const a = auChoix.get(k.id);
      return cochesEffectifs.has(k.id) && a?.cle === 'peut' ? [{ enfant: k, classe: a.classe }] : [];
    }),
    [kids, auChoix, cochesEffectifs],
  );
  const prenomsParticipants = prenomsEnToutesLettres(participants.map((p) => p.enfant.prenom));

  /* --- Les journées cochées ---------------------------------------------- */

  /* Seules comptent les journées de l'école choisie, pour un enfant coché qui peut y être inscrit. */
  const lignes: LigneCommande[] = useMemo(() => {
    const sessions = new Set(ecole?.journees.map((j) => j.sessionId) ?? []);
    const enfants = new Set(participants.map((p) => p.enfant.id));
    return [...choix]
      .map((c) => {
        const [session_id, kid_id] = c.split('|');
        return { session_id, kid_id };
      })
      .filter((l) => sessions.has(l.session_id) && enfants.has(l.kid_id));
  }, [choix, ecole, participants]);
  const { devis, chargement: chiffrage, erreur: erreurDevis } = useDevis(user?.id, lignes, tarifReduit, []);

  const basculer = (sessionId: string, kidId: string) => {
    setErreur(null);
    setChoix((avant) => {
      const apres = new Set(avant);
      const c = cle(sessionId, kidId);
      if (apres.has(c)) apres.delete(c);
      else apres.add(c);
      return apres;
    });
  };

  /* --- La navigation ------------------------------------------------------ */

  const aller = (cible: EtapeDuParcours) => {
    setErreur(null);
    setEtape(cible);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const choisirEcole = (centreId: string) => {
    /* Une autre école : ni ses enfants ni ses journées ne sont ceux qu'on avait cochés. */
    if (centreId !== centre) {
      setChoix(new Set());
      setCoches(null);
    }
    setCentreChoisi(centreId);
  };

  /*
    Le retour de la fiche d'un enfant, vérifiée depuis l'étape 2
    (`?ecole=<centre>&enfant=<id>`) : la même école, l'étape 2, cet enfant
    coché. Lu une fois, puis retiré de l'adresse.
  */
  const reprise = useRef(false);
  useEffect(() => {
    if (reprise.current || chargement || !user) return;
    reprise.current = true;
    const centreRepris = parametres.get('ecole');
    const enfantRepris = parametres.get('enfant');
    if (!centreRepris && !enfantRepris) return;
    if (centreRepris && ecoles.some((e) => e.centreId === centreRepris)) {
      setCentreChoisi(centreRepris);
      setEtape('enfants');
      if (enfantRepris) setCoches(new Set([enfantRepris]));
    }
    setParametres({}, { replace: true });
  }, [chargement, user, ecoles, parametres, setParametres]);

  /* Une étape n'a de sens qu'avec une école ; sans elle, on revient à la première. */
  const etapeAffichee: EtapeDuParcours = ecole ? etape : 'ecole';
  const index = ETAPES_DU_PARCOURS.findIndex((e) => e.cle === etapeAffichee);
  const suivante = ETAPES_DU_PARCOURS[index + 1]?.cle;
  const precedente = ETAPES_DU_PARCOURS[index - 1]?.cle;
  const prete = etapeAffichee === 'ecole' ? ecole !== null
    : etapeAffichee === 'enfants' ? participants.length > 0
      : lignes.length > 0;
  const libelleSuivant = etapeAffichee === 'enfants' && participants.length > 0
    ? `Continuer avec ${prenomsParticipants}`
    : 'Continuer';

  /* --- La commande et le paiement ---------------------------------------- */

  const valider = () => {
    if (!etatProfil.complet) {
      setErreur('Votre profil est incomplet : complétez-le avant d’inscrire vos enfants.');
      return;
    }
    if (lignes.length === 0) {
      setErreur('Cochez au moins une journée pour un enfant.');
      return;
    }
    if (!cgAcceptees) {
      setErreur('Acceptez les conditions générales des journées pédagogiques pour continuer.');
      return;
    }
    if (!devis) {
      setErreur(erreurDevis ?? 'Le montant n’a pas pu être calculé.');
      return;
    }
    setErreur(null);
    setErreurPaiement(null);
    setOffre(offreDePaiement(devis));
    setFenetreOuverte(true);
  };

  /* Le tunnel des stages, à l'identique : c'est le choix du paiement qui écrit. */
  const passerCommande = (): Promise<Commande | null> => {
    const geste = ecritureDuChoix(commande);
    if (geste.forme === 'reprendre') return Promise.resolve(geste.commande);
    if (commandeEnVol.current) return commandeEnVol.current;
    if (!user) {
      setErreurPaiement('Vous devez être connecté pour inscrire vos enfants.');
      return Promise.resolve(null);
    }
    const enVol = (async (): Promise<Commande | null> => {
      try {
        const passee = await commander(
          user.id,
          lignes,
          tarifReduit,
          tarifReduit ? { threshold_cents: SEUIL_REVENUS_CENTIMES, statement_text: texteDeclaration() } : null,
          [],
        );
        setCommande(passee);
        return passee;
      } catch (e) {
        commandeEnVol.current = null;
        setErreurPaiement(e instanceof ErreurDeCommande ? e.message : String(e));
        return null;
      }
    })();
    commandeEnVol.current = enVol;
    return enVol;
  };

  const payerMaintenant = async () => {
    if (enCours) return;
    setEnCours('stripe');
    setErreurPaiement(null);
    try {
      const passee = await passerCommande();
      if (!passee) {
        setEnCours(null);
        return;
      }
      await payerParStripe(passee.invoice_id, confirmationJpa(passee));
    } catch (e) {
      setErreurPaiement(e instanceof ErreurDePaiement ? e.message : String(e));
      setEnCours(null);
    }
  };

  const payerPlusTard = async () => {
    if (enCours) return;
    setEnCours('virement');
    setErreurPaiement(null);
    const passee = await passerCommande();
    if (!passee) {
      setEnCours(null);
      return;
    }
    navigate(confirmationJpa(passee));
  };

  const fermerLaFenetre = () => {
    const sortie = sortieDeLaFenetre(commande, enCours !== null);
    if (sortie.forme === 'attendre') return;
    if (sortie.forme === 'confirmer') {
      navigate(`${sortie.adresse}&panier=garder`);
      return;
    }
    setFenetreOuverte(false);
    setErreurPaiement(null);
  };

  /* ------------------------------------------------------------------------ */

  if (!user) {
    return (
      <div className="container max-w-3xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-bold mb-3">Journées pédagogiques</h1>
        <p className="text-gray-700 mb-6">
          Quand l’école ferme pour une journée pédagogique, Éclosion anime la journée sur place.
          Pour inscrire votre enfant, connectez-vous ou créez votre compte : vous aurez besoin de la
          fiche de votre enfant (santé, personnes autorisées, droit à l’image).
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/login" className="btn-primary">Se connecter</Link>
          <Link to="/register" className="btn-outline">Créer un compte</Link>
        </div>
        <p className="text-sm text-gray-600 mt-6">
          Une fois connecté, revenez ici par le menu « Journées pédagogiques ».
        </p>
      </div>
    );
  }

  /* Le récapitulatif : les journées cochées, enfant par enfant, au montant du devis. */
  const parEnfant = participants
    .map(({ enfant, classe }) => ({
      enfant,
      classe,
      journees: (ecole?.journees ?? []).flatMap((j) => {
        if (!lignes.some((l) => l.session_id === j.sessionId && l.kid_id === enfant.id)) return [];
        const duDevis = devis?.lines.find((l) => l.session_id === j.sessionId && l.kid_id === enfant.id);
        return [{ j, centimes: duDevis?.amount_cents ?? prixDuMoment(j, tarifReduit, maintenant).centimes }];
      }),
    }))
    .filter((e) => e.journees.length > 0);

  /* Le tarif réduit d'une journée cochée, s'il est le même pour toutes. */
  const tarifsReduits = new Set(parEnfant.flatMap((e) => e.journees.map(({ j }) => prixDuMoment(j, true, maintenant).centimes)));
  const tarifReduitUnique = tarifsReduits.size === 1 ? [...tarifsReduits][0] : null;

  const memesHoraires = ecole !== null && ecole.journees.every((j) => horaires(j) === horaires(ecole.journees[0]));

  return (
    <div className="container max-w-3xl mx-auto py-8 px-4 pb-40">
      <h1 className="text-3xl font-bold">Journées pédagogiques</h1>
      <p className="text-gray-700 mt-2">
        Quand l’école ferme pour une journée pédagogique, Éclosion anime la journée sur place, dans l’école.
      </p>

      {!etatProfil.complet && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6 text-amber-900 text-sm">
          Votre profil est incomplet.{' '}
          <Link to="/profile" className="font-semibold underline">Complétez-le</Link> avant d’inscrire vos enfants.
        </div>
      )}

      {chargement ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>
      ) : erreurDeChargement ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-6 text-red-800 flex gap-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <span>Les journées n’ont pas pu être lues : {erreurDeChargement}</span>
        </div>
      ) : ecoles.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-6 mt-6 text-gray-700">
          Aucune journée pédagogique n’est ouverte aux inscriptions pour le moment.
        </div>
      ) : (
        <div className="mt-6 space-y-5">
          <EtapesDuParcours courante={etapeAffichee} onAller={aller} />

          {etapeAffichee !== 'ecole' && ecole && (
            <p className="flex items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-3 text-sm">
              <span className="py-2">
                <span className="text-gray-600">École :</span> <strong>{ecole.ecole}</strong>
                {etapeAffichee !== 'enfants' && participants.length > 0 && (
                  <span className="text-gray-600"> · {prenomsParticipants}</span>
                )}
              </span>
              <button type="button" onClick={() => aller('ecole')} className="min-h-[44px] px-1 font-semibold text-primary-700">
                Changer
              </button>
            </p>
          )}

          {etapeAffichee === 'ecole' && (
            <ChoixDeLEcole ecoles={ecoles} choisi={centre} enfantsParEcole={enfantsParEcole} onChoisir={choisirEcole} />
          )}

          {etapeAffichee === 'enfants' && ecole && annee !== null && (
            <div className="space-y-3">
              <h2 className="text-xl font-extrabold leading-tight">Quels enfants souhaitez-vous inscrire ?</h2>
              <AvertissementsDeLEcole avertissements={avertissementsDeLEcole(ecole.journees)} />
              {kids.length === 0 ? (
                <p className="text-gray-700">
                  Aucun enfant dans votre compte.{' '}
                  <Link to="/kids/new" className="font-semibold text-primary-700 underline">Ajoutez la fiche de votre enfant</Link>.
                </p>
              ) : (
                <div className="grid items-start gap-3 sm:grid-cols-2">
                  {kids.map((k) => {
                    const a = auChoix.get(k.id);
                    if (!a) return null;
                    return (
                      <EnfantACocher
                        key={`${ecole.centreId}-${k.id}`}
                        enfant={k}
                        auChoix={a}
                        coche={cochesEffectifs.has(k.id)}
                        onBasculer={() => basculerEnfant(k.id)}
                        ecole={ecole}
                        annee={annee}
                        classesDuCentre={classesDuCentre}
                        lienDeLaFiche={`/kids/${k.id}/verifier?retour=journees&ecole=${ecole.centreId}`}
                        onRange={async () => {
                          await fetchKids();
                          await relireClassesDesEnfants();
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {etapeAffichee === 'journees' && ecole && (
            <div className="space-y-3">
              <div>
                <h2 className="text-xl font-extrabold leading-tight">Quelles journées ?</h2>
                {memesHoraires && (
                  <p className="mt-1 text-sm text-gray-600 first-letter:uppercase">{horaires(ecole.journees[0])}.</p>
                )}
                {/* Demandé par Éclosion le 26/10/2026, comme dans le courriel de confirmation. */}
                <p className="mt-1 text-sm text-gray-600">
                  <strong className="font-semibold text-gray-800">Pour midi, des tartines : pas de repas chaud.</strong> Et une collation.
                </p>
                <ul className="mt-1 space-y-0.5 text-sm text-gray-600">
                  {ecole.journees.map((j) => (
                    <li key={j.sessionId}>
                      <span className="inline-block font-semibold text-gray-800 first-letter:uppercase">{dateCourte(j.date)}</span> :{' '}
                      {euros(j.prixNormalCentimes)} jusqu’au {instantLisible(j.dateLimite)}, ensuite {euros(j.prixApresLimiteCentimes)}
                      {!memesHoraires && <> ; {horaires(j)}</>}.
                    </li>
                  ))}
                </ul>
              </div>
              {participants.length === 0 ? (
                <p className="text-gray-700">
                  Aucun enfant coché.{' '}
                  <button type="button" onClick={() => aller('enfants')} className="font-semibold text-primary-700 underline">
                    Choisir les enfants
                  </button>
                </p>
              ) : (
                participants.map(({ enfant, classe }) => (
                  <JourneesDeLEnfant
                    key={`${ecole.centreId}-${enfant.id}`}
                    enfant={enfant}
                    classe={classe}
                    journees={ecole.journees}
                    inscrits={inscrits}
                    estCochee={(sessionId) => choix.has(cle(sessionId, enfant.id))}
                    tarifReduit={tarifReduit}
                    maintenant={maintenant}
                    onBasculer={(sessionId) => basculer(sessionId, enfant.id)}
                  />
                ))
              )}
            </div>
          )}

          {etapeAffichee === 'recapitulatif' && ecole && (
            <div className="space-y-4">
              <h2 className="text-xl font-extrabold leading-tight">Votre inscription</h2>
              {parEnfant.length === 0 ? (
                <p className="text-gray-700">
                  Aucune journée cochée.{' '}
                  <button type="button" onClick={() => aller('journees')} className="font-semibold text-primary-700 underline">
                    Choisir les journées
                  </button>
                </p>
              ) : (
                <>
                  <section className="rounded-xl border border-gray-200 bg-white px-4">
                    <p className="pt-3 pb-1 text-xs font-extrabold uppercase tracking-wide text-primary-700">{ecole.ecole}</p>
                    {parEnfant.map(({ enfant, classe, journees: jours }) => (
                      <div key={enfant.id} className="border-t border-gray-100 py-2">
                        <p className="font-extrabold">
                          {enfant.prenom} <span className="text-sm font-semibold text-gray-500">· {classe.libelle}</span>
                        </p>
                        {jours.map(({ j, centimes }) => (
                          <p key={j.sessionId} className="mt-1 flex justify-between gap-3">
                            <span className="first-letter:uppercase">{dateLongue(j.date)}</span>
                            <span className="font-bold">{euros(centimes)}</span>
                          </p>
                        ))}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => aller('journees')}
                      className="min-h-[44px] w-full border-t border-gray-100 text-left text-sm font-semibold text-primary-700"
                    >
                      Modifier les journées
                    </button>
                  </section>

                  <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
                    <label className="flex items-start gap-2.5">
                      <input
                        type="checkbox"
                        checked={tarifReduit}
                        onChange={() => {
                          if (tarifReduit) {
                            setTarifReduit(false);
                            setDeclarationCochee(false);
                          } else {
                            setModaleDeclaration(true);
                          }
                        }}
                        className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>
                        <strong>Je demande le tarif réduit</strong>
                        {tarifReduitUnique !== null && <> : {euros(tarifReduitUnique)} la journée</>}, sur déclaration sur
                        l’honneur, que vous confirmez dans la fenêtre qui s’ouvre.
                      </span>
                    </label>
                    <div className="flex items-baseline justify-between gap-3 border-t border-gray-200 pt-3">
                      <span className="font-bold">
                        Total, {lignes.length} journée{lignes.length > 1 ? 's' : ''}
                      </span>
                      <span className="text-2xl font-extrabold">{chiffrage ? '…' : euros(devis?.amount_cents ?? 0)}</span>
                    </div>
                    {devis && devis.provision_applicable_cents > 0 && (
                      <p className="text-sm text-gray-700">
                        Votre crédit chez Éclosion en paie {euros(devis.provision_applicable_cents)} : reste à payer{' '}
                        {euros(devis.remaining_cents)}.
                      </p>
                    )}
                  </section>

                  <label className="flex items-start gap-2.5 text-sm">
                    <input
                      type="checkbox"
                      checked={cgAcceptees}
                      onChange={(ev) => setCgAcceptees(ev.target.checked)}
                      className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      J’ai lu et j’accepte les{' '}
                      {lienCG ? (
                        <a href={lienCG} target="_blank" rel="noreferrer" className="font-semibold text-primary-700 underline">
                          conditions générales des journées pédagogiques
                        </a>
                      ) : 'conditions générales des journées pédagogiques'}
                      . {REGLE_ANNULATION_JPA}
                    </span>
                  </label>

                  {(erreur || (erreurDevis && lignes.length > 0)) && (
                    <p className="text-sm text-red-700" role="alert">{erreur ?? erreurDevis}</p>
                  )}
                  <button
                    type="button"
                    onClick={valider}
                    disabled={lignes.length === 0 || chiffrage}
                    className="btn-primary w-full min-h-[52px] disabled:opacity-50"
                  >
                    Choisir le paiement
                  </button>
                  <p className="text-center text-sm text-gray-600">
                    Bancontact maintenant, ou virement sous 15 jours. Rien n’est enregistré avant ce choix.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* --- Le bas de page des trois premières étapes : revenir, avancer, le total --- */}
      {!chargement && !erreurDeChargement && ecoles.length > 0 && suivante && (
        <div className="fixed left-0 right-0 bottom-0 z-30 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
          <div className="container max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
            {precedente && (
              <button
                type="button"
                onClick={() => aller(precedente)}
                aria-label="Étape précédente"
                className="btn-outline flex min-h-[52px] items-center justify-center px-3"
              >
                <ArrowLeft className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
            {etapeAffichee === 'journees' && (
              <p className="leading-tight">
                <span className="block text-sm text-gray-600">
                  {lignes.length === 0 ? 'Aucune journée' : `${lignes.length} journée${lignes.length > 1 ? 's' : ''}`}
                </span>
                <span className="block text-xl font-extrabold">{chiffrage ? '…' : euros(devis?.amount_cents ?? 0)}</span>
              </p>
            )}
            <button
              type="button"
              onClick={() => {
                /* L'école proposée devient l'école choisie : une fiche changée en route ne la déplace plus. */
                if (etapeAffichee === 'ecole' && centre) setCentreChoisi(centre);
                aller(suivante);
              }}
              disabled={!prete}
              className="btn-primary flex min-h-[52px] flex-1 items-center justify-center gap-2 disabled:opacity-50"
            >
              <span className="truncate">{libelleSuivant}</span>
              <ArrowRight className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      <Dialog open={modaleDeclaration} onClose={() => setModaleDeclaration(false)} className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-auto p-6">
            <Dialog.Title className="text-lg font-medium mb-4">Déclaration sur l’honneur</Dialog.Title>
            <p className="text-gray-700 mb-4">{texteDeclaration()}</p>
            <label className="flex items-start gap-2 mb-6">
              <input
                type="checkbox"
                checked={declarationCochee}
                onChange={(ev) => setDeclarationCochee(ev.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Je confirme cette déclaration sur l’honneur</span>
            </label>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setModaleDeclaration(false)} className="btn-outline">Annuler</button>
              <button
                type="button"
                onClick={() => {
                  setTarifReduit(true);
                  setModaleDeclaration(false);
                }}
                disabled={!declarationCochee}
                className="btn-primary disabled:opacity-50"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      <ModalePaiement
        isOpen={fenetreOuverte}
        onClose={fermerLaFenetre}
        onPayerMaintenant={() => void payerMaintenant()}
        onPayerPlusTard={() => void payerPlusTard()}
        offre={offre}
        enCours={enCours}
        erreur={erreurPaiement}
        ceQuiResteEnFermant="vos journées restent cochées"
      />
    </div>
  );
}
